import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import '@/App.css';

// Pages
import LoginPage from './pages/LoginPage';
import EmployeeDashboard from './pages/EmployeeDashboard';
import ManagerDashboard from './pages/ManagerDashboard';
import WorkClockPage from './pages/WorkClockPage';
import ManagerClockDashboard from './pages/ManagerClockDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CorporateCalendar from './pages/CorporateCalendar';
import VacationsComingSoon from './pages/VacationsComingSoon';
import VacationBalanceAdmin from './pages/VacationBalanceAdmin';
import VacationRecordsAdmin from './pages/VacationRecordsAdmin';
import VacationRequestForm from './pages/VacationRequestForm';
import RemoteWorkForm from './pages/RemoteWorkForm';
import MyRequests from './pages/MyRequests';
import ApprovalsPage from './pages/ApprovalsPage';
// Informe IA deshabilitado temporalmente (ver App.js) — import comentado, no se borra el fichero.
// import AIReportPage from './pages/AIReportPage';
import UserManagement from './pages/UserManagement';
import HolidayManagement from './pages/HolidayManagement';
import CalendarManagement from './pages/CalendarManagement';
import AuditLogPage from './pages/AuditLogPage';
import AuthCallback from './pages/AuthCallback';
import AuthPopupPage from './pages/AuthPopupPage';
import EmployeePortal from './pages/EmployeePortal';
import DataImportPage from './pages/DataImportPage';
import ConveniosManagement from './pages/ConveniosManagement';
import PersonalDayForm from './pages/PersonalDayForm';
import PersonalDaysReport from './pages/PersonalDaysReport';
import LogaExportPage from './pages/LogaExportPage';
import TeamOverridesAdmin from './pages/TeamOverridesAdmin';
import DirectoryEmbed from './pages/DirectoryEmbed';
import ClockIntegrationAdmin from './pages/ClockIntegrationAdmin';
import PhotoUploadPage from './pages/PhotoUploadPage';
import UserOverridesAdmin from './pages/UserOverridesAdmin';

// Layout
import Layout from './components/Layout';

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-3">
      <div className="h-8 w-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
      <span className="text-sm text-muted-foreground">Cargando...</span>
    </div>
  </div>
);

const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

const DashboardRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'admin') return <Navigate to="/admin" replace />;
  if (user.role === 'manager') return <Navigate to="/manager" replace />;
  return <Navigate to="/portal" replace />;
};

const VacationGate = () => {
  const { user } = useAuth();
  if (!user) return null;
  if (!user.vacations_enabled) return <VacationsComingSoon />;
  if (!user.fecha_inicio) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <svg className="h-8 w-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <h2 className="text-xl font-bold font-heading mb-2">Solicitudes no disponibles</h2>
          <p className="text-sm text-muted-foreground mb-2">
            Tu puesto y calendario aún no están formalizados en el sistema (falta tu <strong>fecha de inicio</strong>).
          </p>
          <p className="text-sm text-muted-foreground">
            Por favor, contacta con <strong>Admón. de Personal</strong> para activar el módulo de vacaciones.
          </p>
        </div>
      </div>
    );
  }
  return <VacationRequestForm />;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <DashboardRedirect /> : <LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/popup" element={<AuthPopupPage />} />
      <Route path="/directory-embed" element={<DirectoryEmbed />} />
      <Route path="/photo-upload" element={<PhotoUploadPage />} />
      <Route path="/portal" element={<ProtectedRoute><EmployeePortal /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardRedirect />} />
        <Route path="dashboard" element={<EmployeeDashboard />} />
        <Route path="request-timeoff" element={<VacationGate />} />
        <Route path="manager" element={<ProtectedRoute roles={['manager', 'admin']}><ManagerDashboard /></ProtectedRoute>} />
        <Route path="admin" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
        <Route path="calendar" element={<CorporateCalendar />} />
        <Route path="vacations/new" element={<VacationGate />} />
        <Route path="personal-days/new" element={<PersonalDayForm />} />
        <Route path="remote/new" element={<RemoteWorkForm />} />
        <Route path="clock" element={<WorkClockPage />} />
        <Route path="clock/team" element={<ManagerClockDashboard />} />
        <Route path="my-requests" element={<MyRequests />} />
        <Route path="approvals" element={<ProtectedRoute roles={['manager', 'admin']}><ApprovalsPage /></ProtectedRoute>} />
        {/* Informe IA: deshabilitado temporalmente a petición de RRHH (errata "anual"/"mensual" en bolsa
            de teletrabajo, ver hilo "Flex - Teletrabajo duda"). Código y endpoint backend intactos.
            Para reactivar: descomentar esta ruta + los accesos en Layout.js, EmployeeDashboard.js,
            AdminDashboard.js y EmployeePortal.js (buscar "Informe IA deshabilitado"). */}
        {/* <Route path="ai-report" element={<AIReportPage />} /> */}
        <Route path="users" element={<ProtectedRoute roles={['admin']}><UserManagement /></ProtectedRoute>} />
        <Route path="holidays" element={<ProtectedRoute roles={['admin']}><HolidayManagement /></ProtectedRoute>} />
        <Route path="calendars" element={<ProtectedRoute roles={['admin']}><CalendarManagement /></ProtectedRoute>} />
        <Route path="convenios" element={<ConveniosManagement />} />
        <Route path="personal-days-report" element={<ProtectedRoute roles={['admin']}><PersonalDaysReport /></ProtectedRoute>} />
        <Route path="loga-export" element={<ProtectedRoute roles={['admin']}><LogaExportPage /></ProtectedRoute>} />
        <Route path="audit-log" element={<ProtectedRoute roles={['admin']}><AuditLogPage /></ProtectedRoute>} />
        <Route path="import" element={<ProtectedRoute roles={['admin']}><DataImportPage /></ProtectedRoute>} />
        <Route path="vacation-balance-admin" element={<ProtectedRoute roles={['admin']}><VacationBalanceAdmin /></ProtectedRoute>} />
        <Route path="vacation-records-admin" element={<ProtectedRoute roles={['admin']}><VacationRecordsAdmin /></ProtectedRoute>} />
        <Route path="team-overrides" element={<ProtectedRoute roles={['admin']}><TeamOverridesAdmin /></ProtectedRoute>} />
        <Route path="user-overrides" element={<ProtectedRoute roles={['admin']}><UserOverridesAdmin /></ProtectedRoute>} />
        <Route path="directory" element={<Navigate to="/dashboard" replace />} />
        <Route path="clock-integration" element={<ProtectedRoute roles={['admin']}><ClockIntegrationAdmin /></ProtectedRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <LanguageProvider>
        <AuthProvider>
          <NotificationProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          </NotificationProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
